import { ChevronDown } from 'lucide-react'
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export function CollapsibleSection({
  children,
  collapsedHeight = 150,
  fadeClassName = 'from-card',
  defaultCollapsed = true,
}: {
  children: ReactNode
  collapsedHeight?: number
  /** Tailwind `from-*` color the bottom gradient fades to (match the card background). */
  fadeClassName?: string
  defaultCollapsed?: boolean
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const [overflowing, setOverflowing] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = contentRef.current
    if (!el) return
    const check = () => setOverflowing(el.scrollHeight > collapsedHeight)
    check()
    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [collapsedHeight])

  const isCollapsed = collapsed && overflowing

  return (
    <div className="relative">
      {overflowing && (
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground absolute top-0 right-0 z-10 size-6"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
        >
          <ChevronDown className={cn('size-4 transition-transform', !isCollapsed && 'rotate-180')} />
        </Button>
      )}
      <div
        className={cn('overflow-hidden', isCollapsed && 'cursor-pointer')}
        style={isCollapsed ? { maxHeight: collapsedHeight } : undefined}
        onClick={isCollapsed ? () => setCollapsed(false) : undefined}
      >
        <div ref={contentRef}>{children}</div>
      </div>
      {isCollapsed && (
        <div
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-linear-to-t to-transparent',
            fadeClassName
          )}
        />
      )}
    </div>
  )
}
